import { MemoryRouter, Route, Routes, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { useCanvasContext } from '@/embed/context';
import { EmbedMessage } from '@/embed/EmbedMessage';
import { ActionCard } from '@/embed/ActionCard';
import { Editor } from '@/proposals/editor/Editor';
import { listProposals, createProposal } from '@/proposals/lib/api';
import { newProposalDocument } from '@/proposals/lib/template';

const UNTITLED = 'Untitled proposal';
import type { EmbedWidgetProps } from '@/embed/types';

/** The proposals for this opportunity + a way to start one, at the router base. */
function ProposalListInline({ opportunityId }: { readonly opportunityId: string }): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const proposals = useQuery({
    queryKey: ['embed-proposals', opportunityId],
    queryFn: () => listProposals(opportunityId),
  });

  // The server seeds the client, event and salesperson from the opportunity.
  const create = useMutation({
    mutationFn: () => createProposal({ opportunityId, document: newProposalDocument(UNTITLED) }),
    onSuccess: (proposal) => {
      void queryClient.invalidateQueries({ queryKey: ['embed-proposals', opportunityId] });
      navigate(`/proposals/${proposal.id}`);
    },
  });

  return (
    <div className="p-3">
      <ActionCard title="Proposals" description="Build a client proposal from the BCE template.">
        <div className="space-y-2">
          <Button variant="primary" onClick={() => create.mutate()} disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'New proposal'}
          </Button>
          {create.isError ? (
            <p className="text-portal-micro text-rose-600">{errorMessage(create.error)}</p>
          ) : null}

          {proposals.isPending ? (
            <p className="text-portal-small text-portal-muted">Loading proposals…</p>
          ) : proposals.isError ? (
            <p className="text-portal-small text-rose-600">{errorMessage(proposals.error)}</p>
          ) : proposals.data.length === 0 ? (
            <p className="text-portal-small text-portal-muted">No proposals for this opportunity yet.</p>
          ) : (
            <ul className="divide-y divide-portal-line">
              {proposals.data.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className="w-full py-2 text-left text-portal-small font-bold text-primary hover:underline"
                    onClick={() => navigate(`/proposals/${p.id}`)}
                  >
                    {p.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </ActionCard>
    </div>
  );
}

/**
 * The Salesforce embed's Proposal tab: the full proposal generator inline on the
 * Opportunity, gated by the same `generate_proposals` permission (the API 403s
 * anyone without it). Mirrors the contract tab — a scoped router hosting the
 * opportunity's proposal list and the editor. Place it on a dedicated, tall
 * Opportunity tab (`opportunity-proposal`).
 *
 * @param props - The Salesforce Opportunity record id.
 * @returns The inline proposal workflow.
 */
export function OpportunityProposal({ externalId }: EmbedWidgetProps): React.ReactElement {
  const ctx = useCanvasContext('Opportunity', externalId);

  if (ctx.isPending) return <EmbedMessage text="Loading proposals…" />;
  if (ctx.isError) {
    return <EmbedMessage tone="error" text={`Couldn't load this opportunity. ${errorMessage(ctx.error)}`} />;
  }

  // Cancel the embed shell's padding and fill the canvas iframe — the editor is
  // a full-viewport app and needs the height.
  return (
    <div className="-m-3 flex h-screen flex-col bg-background">
      <MemoryRouter initialEntries={['/proposals']}>
        <Routes>
          <Route path="/proposals" element={<ProposalListInline opportunityId={externalId} />} />
          <Route path="/proposals/:id" element={<Editor />} />
        </Routes>
      </MemoryRouter>
    </div>
  );
}
